import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import useScrollToTop from '../../hooks/useScrollToTop';
import ImageCarousel from '../../components/shared/ImageCarousel';

const SantaCruz: React.FC = () => {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  useScrollToTop();

  const images = [
    { src: '/images/hillcrest_1.png', alt: 'Santa Cruz Villa 1' },
    { src: '/images/hillcrest_2.png', alt: 'Santa Cruz Villa 2' },
    { src: '/images/hillcrest_3.png', alt: 'Santa Cruz Villa 3' },
    { src: '/images/hillcrest_4.png', alt: 'Santa Cruz Villa 4' },
    { src: '/images/hillcrest_5.png', alt: 'Santa Cruz Villa 5' },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5))',
          backgroundColor: '#1976d2',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          height: '40vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          textAlign: 'center',
        }}
      >
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="h2" component="h1" gutterBottom>
              {t('projects.hillcrest.title')}
            </Typography>
            <Typography variant="h5" gutterBottom>
              {t('projects.hillcrest.subtitle')}
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Project Overview */}
      <Container sx={{ py: 8 }}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Typography variant="h4" gutterBottom>
                {t('projects.hillcrest.overview.title')}
              </Typography>
              <Typography variant="body1" paragraph>
                {t('projects.hillcrest.overview.paragraph1')}
              </Typography>
              <Typography variant="body1" paragraph>
                {t('projects.hillcrest.overview.paragraph2')}
              </Typography>
              <Typography variant="body1" paragraph>
                {t('projects.hillcrest.overview.paragraph3')}
              </Typography>
            </motion.div>
          </Grid>
        </Grid>
      </Container>

      {/* Key Metrics */}
      <Box sx={{ bgcolor: 'background.paper', py: 8 }}>
        <Container>
          <Typography variant="h4" gutterBottom>
            {t('projects.hillcrest.metrics.title')}
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('projects.hillcrest.metrics.developmentSize')}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {t('projects.hillcrest.metrics.developmentSizeValue')}
                  </Typography>
                  
                  <Typography variant="h6" gutterBottom>
                    {t('projects.hillcrest.metrics.totalUnits')}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {t('projects.hillcrest.metrics.totalUnitsValue')}
                  </Typography>
                  
                  <Typography variant="h6" gutterBottom>
                    {t('projects.hillcrest.metrics.developmentCost')}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {t('projects.hillcrest.metrics.developmentCostValue')}
                  </Typography>
                  
                  <Typography variant="h6" gutterBottom>
                    {t('projects.hillcrest.metrics.totalValue')}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {t('projects.hillcrest.metrics.totalValueValue')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('projects.hillcrest.metrics.eb5Funding')}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {t('projects.hillcrest.metrics.eb5FundingValue')}
                  </Typography>
                  
                  <Typography variant="h6" gutterBottom>
                    {t('projects.hillcrest.metrics.minimumInvestment')}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {t('projects.hillcrest.metrics.minimumInvestmentValue')}
                  </Typography>
                  
                  {/* <Typography variant="h6" gutterBottom>
                    {t('projects.hillcrest.metrics.administrativeFee')}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {t('projects.hillcrest.metrics.administrativeFeeValue')}
                  </Typography> */}
                  
                  <Typography variant="h6" gutterBottom>
                    {t('projects.hillcrest.metrics.projectStatus')}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {t('projects.hillcrest.metrics.projectStatusValue')}
                  </Typography> 
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Amenities */}
      <Container sx={{ py: 8 }}>
        <Typography variant="h4" gutterBottom>
          {t('projects.hillcrest.amenities.title')}
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <List>
                <ListItem>
                  <ListItemText primary={t('projects.hillcrest.amenities.item1')} />
                </ListItem>
                <ListItem>
                  <ListItemText primary={t('projects.hillcrest.amenities.item2')} />
                </ListItem>
                <ListItem>
                  <ListItemText primary={t('projects.hillcrest.amenities.item3')} />
                </ListItem>
                <ListItem>
                  <ListItemText primary={t('projects.hillcrest.amenities.item4')} />
                </ListItem>
              </List>
            </motion.div>
          </Grid>
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <List>
                <ListItem>
                  <ListItemText primary={t('projects.hillcrest.amenities.item5')} />
                </ListItem>
                <ListItem>
                  <ListItemText primary={t('projects.hillcrest.amenities.item6')} />
                </ListItem>
                <ListItem>
                  <ListItemText primary={t('projects.hillcrest.amenities.item7')} />
                </ListItem>
                <ListItem>
                  <ListItemText primary={t('projects.hillcrest.amenities.item8')} />
                </ListItem>
              </List>
            </motion.div>
          </Grid>
        </Grid>
      </Container>

      {/* Location Highlights */}
      <Box sx={{ bgcolor: 'background.paper', py: 8 }}>
        <Container>
          <Typography variant="h4" gutterBottom>
            {t('projects.hillcrest.location.title')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('projects.hillcrest.location.description')}
          </Typography>
        </Container>
      </Box>

      {/* Santa Cruz Images */}
      <Box sx={{ py: 4 }}>
        <Container>
          <ImageCarousel images={images} />
        </Container>
      </Box>

      {/* Back to EB5 Button */}
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Container>
          <Button
            component={Link}
            to="/us-green-card/eb5"
            variant="contained"
            color="primary"
            size="large"
          >
            {t('projects.hillcrest.backToEB5')}
          </Button>
        </Container>
      </Box>
    </Box>
  );
};

export default SantaCruz;
